import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { OTPInput } from '@/components/auth';
import { useAuth, useTelegram } from '@/hooks';
import { useAuthStore } from '@/store/authStore';

export const OTPPage: React.FC = () => {
  const navigate = useNavigate();
  const { confirmOTP, login, isLoading, error, clearError } = useAuth();
  const { pendingEmail, isAuthenticated } = useAuthStore();
  const { showBackButton, hideBackButton, hideMainButton } = useTelegram();

  // Redirect if no pending email
  useEffect(() => {
    if (!pendingEmail) {
      navigate('/login', { replace: true });
    }
  }, [pendingEmail, navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/signals', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Set up back button
  useEffect(() => {
    showBackButton(() => {
      clearError();
      navigate('/login');
    });
    hideMainButton();

    return () => {
      hideBackButton();
    };
  }, [showBackButton, hideBackButton, hideMainButton, clearError, navigate]);

  const handleComplete = async (code: string) => {
    if (!pendingEmail) return;

    const result = await confirmOTP(pendingEmail, code);
    if (result) {
      navigate('/signals', { replace: true });
    }
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    clearError();
    await login(pendingEmail);
  };

  if (!pendingEmail) {
    return null;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
        backgroundColor: 'var(--tg-theme-bg-color)',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginTop: '40px', marginBottom: '48px' }}>
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: 'var(--tg-theme-button-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: '28px',
          }}
        >
          ✉️
        </div>
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: 'var(--tg-theme-text-color)',
            margin: '0 0 8px',
          }}
        >
          Enter Verification Code
        </h1>
      </div>

      {/* OTP Input */}
      <OTPInput
        onComplete={handleComplete}
        isLoading={isLoading}
        error={error}
        email={pendingEmail}
        onResend={handleResend}
      />
    </div>
  );
};
